import React from 'react';

import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const ProjectDetailsStep = ({ formData, updateFormData, errors }) => {
  const projectTypes = [
    {
      id: 'solar',
      name: 'Zonnepanelen',
      icon: 'Sun',
      description: 'Installatie van zonnepanelen',
      services: ['Zonnepanelen Installatie', 'Zonnepanelen + Batterij', 'Zonnepanelen Onderhoud']
    },
    {
      id: 'heatpump',
      name: 'Warmtepomp',
      icon: 'Thermometer',
      description: 'Warmtepomp installatie',
      services: ['Lucht-water Warmtepomp', 'Bodem Warmtepomp', 'Hybride Warmtepomp']
    },
    {
      id: 'insulation',
      name: 'Isolatie',
      icon: 'Home',
      description: 'Isolatie werkzaamheden',
      services: ['Spouwmuurisolatie', 'Dakisolatie', 'Vloerisolatie', 'Gevelisolatie']
    },
    {
      id: 'battery',
      name: 'Thuisbatterij',
      icon: 'Battery',
      description: 'Energie opslag systemen',
      services: ['Thuisbatterij Installatie', 'Batterij Uitbreiding', 'Batterij Vervanging']
    }
  ];

  const handleProjectTypeSelect = (typeId) => {
    const selectedType = projectTypes?.find(t => t?.id === typeId);
    updateFormData({
      projectType: typeId,
      serviceType: '' // Reset service type when project type changes
    });
  };

  const selectedProjectType = projectTypes?.find(t => t?.id === formData?.projectType);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Projectdetails</h2>
        <p className="text-muted-foreground">Vertel ons over uw verduurzamingsproject</p>
      </div>

      {/* Project Type Selection */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Wat voor project heeft u in gedachten? <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {projectTypes?.map(type => (
            <button
              key={type?.id}
              type="button"
              onClick={() => handleProjectTypeSelect(type?.id)}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:shadow-md",
                formData?.projectType === type?.id
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              <Icon name={type?.icon} size={32} className={formData?.projectType === type?.id ? "text-primary" : "text-muted-foreground"} />
              <span className={cn(
                "text-sm font-medium mt-2 text-center",
                formData?.projectType === type?.id ? "text-primary" : "text-foreground"
              )}>
                {type?.name}
              </span>
              <span className="text-xs text-muted-foreground text-center mt-1">{type?.description}</span>
            </button>
          ))}
        </div>
        {errors?.projectType && (
          <p className="text-sm text-destructive mt-2">{errors?.projectType}</p>
        )}
      </div>

      {/* Service Type Selection (Conditional) */}
      {selectedProjectType && (
        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Welke specifieke dienst heeft u nodig? <span className="text-destructive">*</span>
          </label>
          <div className="space-y-2">
            {selectedProjectType?.services?.map(service => (
              <label
                key={service}
                className={cn(
                  "flex items-center p-3 rounded-lg border cursor-pointer transition-all",
                  formData?.serviceType === service
                    ? "border-primary bg-primary/5" :"border-border bg-card hover:border-primary/50"
                )}
              >
                <input
                  type="radio"
                  name="serviceType"
                  value={service}
                  checked={formData?.serviceType === service}
                  onChange={(e) => updateFormData({ serviceType: e?.target?.value })}
                  className="h-4 w-4 text-primary focus:ring-primary"
                />
                <span className="ml-3 text-sm text-foreground">{service}</span>
              </label>
            ))}
          </div>
          {errors?.serviceType && (
            <p className="text-sm text-destructive mt-2">{errors?.serviceType}</p>
          )}
        </div>
      )}

      {/* Project Description */}
      <div>
        <label htmlFor="projectDescription" className="text-sm font-medium text-foreground mb-2 block">
          Beschrijf uw project <span className="text-destructive">*</span>
        </label>
        <textarea
          id="projectDescription"
          rows={5}
          value={formData?.projectDescription}
          onChange={(e) => updateFormData({ projectDescription: e?.target?.value })}
          placeholder="Beschrijf uw project zo gedetailleerd mogelijk. Bijvoorbeeld: huidige situatie, gewenste oplossing, specifieke wensen..."
          className={cn(
            "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            errors?.projectDescription && "border-destructive focus-visible:ring-destructive"
          )}
        />
        {errors?.projectDescription && (
          <p className="text-sm text-destructive mt-2">{errors?.projectDescription}</p>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          <Icon name="Info" size={12} className="inline mr-1" />
          Hoe meer details u geeft, hoe beter providers u kunnen helpen
        </p>
      </div>

      {/* Photo Upload Section */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Foto's van uw woning (optioneel)
        </label>
        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
          <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-foreground mb-1">Sleep foto's hierheen of klik om te uploaden</p>
          <p className="text-xs text-muted-foreground">PNG, JPG tot 10MB (max 5 foto's)</p>
        </div>
        {formData?.propertyPhotos?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {formData?.propertyPhotos?.map((photo, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border">
                <img src={photo} alt={`Property ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailsStep;